import React, { useCallback, useEffect, useRef, useState } from "react";

import { Button, Popover, Stack, TextField, Typography } from "@mui/material";

import { RoleName } from "constants";
import { IRole } from "store/types";

// ── Local design tokens (sourced from storybook/src/utils — do not import) ──
const CHIP_BG = "#edf1f9";
const CHIP_LABEL_COLOR = "#496682";
const CHIP_VALUE_COLOR = "#071F35";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ClientCharacteristicsChipProps {
  label: string;
  value: string | null;
  role: IRole | null;
  onSave: (val: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ClientCharacteristicsChip: React.FC<ClientCharacteristicsChipProps> = ({
  label,
  value,
  role,
  onSave,
}) => {
  // Self-contained popover state management (Characteristics-bar pattern)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [inputValue, setInputValue] = useState(value ?? "");
  const inputRef = useRef<HTMLInputElement>(null);

  const isEditable = role?.name === RoleName.Admin || role?.name === RoleName.Analyst;
  const displayValue = value || "N/A";
  const open = Boolean(anchorEl);

  // Testid helper: replace whitespace with hyphens
  const labelId = label.replace(/\s+/g, "-");

  // Sync input when value changes (e.g. optimistic update re-render)
  useEffect(() => {
    if (!open) {
      setInputValue(value ?? "");
    }
  }, [value, open]);

  // Focus input when popover opens
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!isEditable) return;
    setAnchorEl(e.currentTarget);
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSave = useCallback(() => {
    onSave(inputValue);
    handleClose();
  }, [onSave, inputValue, handleClose]);

  return (
    <>
      <Button
        data-testid={`characteristic-chip-${labelId}`}
        data-editable={String(isEditable)}
        sx={{
          color: CHIP_LABEL_COLOR,
          padding: "0 10px",
          minWidth: "auto",
          fontSize: "15px",
          backgroundColor: CHIP_BG,
          minHeight: "28px",
          maxHeight: "28px",
          textTransform: "none",
          cursor: isEditable ? "pointer" : "default",
          "&:hover": {
            backgroundColor: isEditable ? "#dce3f0" : CHIP_BG,
          },
        }}
        onClick={handleButtonClick}
        disableElevation
        className="trigger"
      >
        <Stack
          direction="row"
          sx={{
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
            maxWidth: "200px",
            fontSize: "12px",
            fontWeight: "400",
          }}
          gap="5px"
        >
          <Typography sx={{ color: CHIP_LABEL_COLOR, fontSize: "12px" }}>
            {label}:
          </Typography>
          <Typography
            sx={{
              color: CHIP_VALUE_COLOR,
              fontSize: "12px",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            {displayValue}
          </Typography>
        </Stack>
      </Button>
      {isEditable && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          data-testid={`popover-${labelId}`}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <Stack sx={{ p: 2, gap: 1.5, minWidth: "220px" }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: CHIP_VALUE_COLOR }}>
              {label}
            </Typography>
            <TextField
              inputProps={{ "data-testid": "popover-input" }}
              inputRef={inputRef}
              size="small"
              fullWidth
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") handleClose();
                if (e.key === "Enter") handleSave();
              }}
              autoFocus
            />
            <Stack direction="row" justifyContent="flex-end" gap={1}>
              <Button size="small" variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                data-testid="popover-save"
                onClick={handleSave}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        </Popover>
      )}
    </>
  );
};
